// mbscan manager-client の同名部品と共通。片側だけで変えない

import { isSome } from '@mbfab/shared'
import type { ComponentProps } from 'react'
import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

import { CompactFormInput } from '@/components/ui/form'

/**
 * 入力が止まってから適用するまでの待ち時間。全画面でこの1定数を使う。
 * 待ちを画面ごとに実装させると、付け忘れた画面がキー入力のたびに一覧を再取得するため、この部品に閉じ込めている
 */
export const filterInputDelayMs = 400

/**
 * FilterBar と配下の FilterTextInput をつなぐ窓口。入力中の文字列は各入力欄の中に閉じているため、
 * - reportPending: 「入力待ち・変換中（表示中の文字列がまだ適用されていない）」を集め、FilterBar の `onPendingChange` で画面へ伝える。
 *   画面側は「表示中の条件と一覧が一致していない間は一括操作を止める」判定をこれで行う
 * - clearGeneration: 「すべてクリア」のたびに増える。検証で弾かれた値は適用済みの値が変わらないため、
 *   value の変化だけでは入力欄が戻らない。これが変わったら入力欄を適用済みの値へ戻す
 */
export const FilterBarContext = createContext<
  { reportPending: (id: string, pending: boolean) => void; clearGeneration: number } | undefined
>(undefined)

type FilterTextInputProps = Omit<
  ComponentProps<typeof CompactFormInput>,
  'value' | 'defaultValue' | 'onChange' | 'accept'
> & {
  /**
   * 適用済みの値。チップ解除・すべてクリアなどで外から変わったときは入力欄もこの値に戻る。
   * `onValueChange` で受け取った値を整形（trim 等）して返さないこと。入力中の文字列が整形後の値で上書きされる
   */
  value: string
  /**
   * 入力が {@link filterInputDelayMs} 止まったとき・Enter・クリア時にだけ呼ばれる（キー入力ごとには呼ばれない）。
   * 検証で弾いて value を変えなければ、入力欄は弾かれた文字列を表示したまま残る
   */
  onValueChange: (value: string) => void
  /** 打たせない文字の判定（数字だけの欄など）。false を返した入力は入力欄に反映しない */
  accept?: (next: string) => boolean
}

/**
 * 一覧の絞り込みに使うテキスト入力（文字・数値の入力すべて）。
 * 入力中の文字列はこの部品の中だけで持ち、タイピングがひと段落してから `onValueChange` を呼ぶ。
 * 日本語の変換中は適用せず、変換確定から待ち直す。
 *
 * 絞り込みのテキスト入力に `CompactFormInput` の onChange を直接つなぐと毎キー通信になるので、
 * 必ずこの部品を使う（lint の `filter/no-raw-text-input` が FilterBar を使うファイルで検出する）
 */
export function FilterTextInput({
  value,
  onValueChange,
  accept,
  onCompositionStart,
  onCompositionEnd,
  onKeyDown,
  clearable = true,
  type = 'text',
  ...props
}: FilterTextInputProps) {
  const [draft, setDraft] = useState(value)
  const composing = useRef(false)
  // Safari は compositionend の後に変換確定の Enter の keydown を isComposing=false で送るため、その Enter を無視する目印
  const justComposed = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // 最後に親へ渡した値。親の value がこれと違うときだけ外からの変更（チップ解除など）とみなす
  const committed = useRef(value)
  // 親が検証で弾いて value を変えなかった場合も、同じ文字列での再確定（Enter）を通すため、省略の判定は親の value で行う
  const latestValue = useRef(value)
  const latestOnValueChange = useRef(onValueChange)
  // 描画中に ref を書き換えない（lint）。描画直後に同期的に反映するため useLayoutEffect を使い、
  // 後続の effect（value の変化・クリア）が最新値を読めるよう、それらより前に宣言する
  useLayoutEffect(() => {
    latestValue.current = value
    latestOnValueChange.current = onValueChange
  })
  const [pending, setPending] = useState(false)
  const filterBar = useContext(FilterBarContext)
  const reportPending = filterBar?.reportPending
  const clearGeneration = filterBar?.clearGeneration ?? 0
  const id = useId()

  useEffect(() => {
    reportPending?.(id, pending)
  }, [reportPending, id, pending])
  useEffect(
    () => () => {
      reportPending?.(id, false)
    },
    [reportPending, id],
  )

  const cancelPending = () => {
    clearTimeout(timer.current)
    timer.current = undefined
  }
  const commit = (next: string) => {
    cancelPending()
    setPending(false)
    committed.current = next
    if (next !== latestValue.current) {
      latestOnValueChange.current(next)
    }
  }
  const schedule = (next: string) => {
    cancelPending()
    // 適用済みの値に戻しただけなら待つものは無い
    setPending(next !== latestValue.current)
    timer.current = setTimeout(() => {
      commit(next)
    }, filterInputDelayMs)
  }
  const resetTo = (next: string) => {
    committed.current = next
    cancelPending()
    setPending(false)
    setDraft(next)
  }

  useEffect(() => {
    if (value === committed.current) {
      return
    }
    // チップ解除・すべてクリア・URL 遷移などで適用済みの値が外から変わった。待機中の入力は捨てて表示を合わせる
    resetTo(value)
  }, [value])

  const handledClearGeneration = useRef(clearGeneration)
  useEffect(() => {
    if (clearGeneration === handledClearGeneration.current) {
      return
    }
    handledClearGeneration.current = clearGeneration
    resetTo(latestValue.current)
  }, [clearGeneration])

  // 離脱時に待機中の入力を適用しない（画面遷移の途中で検索条件の navigate が走るのを避ける）
  useEffect(() => cancelPending, [])

  return (
    <CompactFormInput
      {...props}
      type={type}
      clearable={clearable}
      value={draft}
      onChange={(event) => {
        const next = event.target.value
        if (isSome(accept) && !accept(next)) {
          return
        }
        setDraft(next)
        if (composing.current) {
          return
        }
        // 数値欄で「-」「1e」など打ちかけの間は value が空になる。空入力として即時解除せず、続きを待つ
        if (event.target.validity.badInput) {
          cancelPending()
          setPending(true)
          return
        }
        // 空にした（クリアボタン・全選択削除）ときは待たずに解除する。条件を外す操作は確定の意思が明確なため
        if (next === '') {
          commit(next)
          return
        }
        schedule(next)
      }}
      onCompositionStart={(event) => {
        composing.current = true
        cancelPending()
        setPending(true)
        onCompositionStart?.(event)
      }}
      onCompositionEnd={(event) => {
        composing.current = false
        justComposed.current = true
        setTimeout(() => {
          justComposed.current = false
        }, 0)
        const next = event.currentTarget.value
        setDraft(next)
        schedule(next)
        onCompositionEnd?.(event)
      }}
      onKeyDown={(event) => {
        // 変換確定の Enter では適用しない
        if (event.key === 'Enter' && !event.nativeEvent.isComposing && !justComposed.current) {
          commit(event.currentTarget.value)
        }
        onKeyDown?.(event)
      }}
    />
  )
}
